import { useEffect, useState } from "react";
import MonitorService from "../../services/monitor.service";
import Table, { Column } from "../common/table";
import { Monitor } from "../../types/Monitor";
import { useNavigate, useParams } from "react-router-dom";
import { Condition } from "../../types/Condition";
import Breadcrumb from "../common/breadcrumb/breadcrumb";
import { useTranslation } from "react-i18next";
import MultiSelect from "../common/MultiSelect";
import FleetsService from "../../services/fleets.services";
import { DropdownOption } from "../../models/DropdownOption";
import { toastSuccess } from "../../App";

export default function MonitorPage() {
  const { t } = useTranslation();

  const columns: Column<any>[] = [
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('monitors.priority')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="px-6 py-2 text-left text-xs text-gray-500">
          P{row.priority}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('monitors.name')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        return (
          <td key={colI} className="whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500">
            {row.name}
          </td>
        )
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="hide-on-mobile bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('monitors.description')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => (
        <td key={colI} className="hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500">
          {row.description}
        </td>
      ),
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('monitors.muted')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        return (
          <td key={colI} className="hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500">
            <input
              type="checkbox"
              checked={row?.muted}
              onClick={(e) => e.stopPropagation()}
              className="mt-1 w-5 h-5 rounded-md border border-gray-300"
              onChange={() => handleCheckboxClick(row._id, 'muted', !row.muted)}
            />
          </td>
        )
      },
    },
    {
      renderHeader: (i: number) => (
        <th
          key={i}
          className="bg-gray-50 px-6 py-2 text-left text-sm font-semibold text-gray-900"
          scope="col"
        >
          {t('monitors.disabled')}
        </th>
      ),
      renderRow: (row, _rowI, colI) => {
        return (
          <td key={colI} className="hide-on-mobile whitespace-nowrap px-6 py-2 text-left text-xs text-gray-500">
            <input
              type="checkbox"
              checked={row?.disabled}
              onClick={(e) => e.stopPropagation()}
              className="mt-1 w-5 h-5 rounded-md border border-gray-300"
              onChange={() => handleCheckboxClick(row._id, 'disabled', !row.disabled)}
            />
          </td>
        )
      },
    },
  ];

  const navigate = useNavigate();
  const { monitorId } = useParams();
  const [monitor, setMonitor] = useState<Monitor>();
  const [rows, setRows] = useState<Condition[]>([]);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [muted, setMuted] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [fleets, setFleets] = useState<DropdownOption<string>[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<DropdownOption<string>[]>([]);


  const canSave = name.trim() !== '' && description.trim() !== '';

  const breadcrumbPages = [
    { name: 'Monitors List', href: '/monitors', current: false, isHome: true },
    { name: t('monitors.monitorDetail'), href: `/monitors/${monitorId}`, current: false },
  ];

  const handleCheckboxClick = (conditionId: string, field: 'muted' | 'disabled', value: boolean) => {
    if (monitorId) {
      MonitorService.updateCondition(monitorId, conditionId, { [field]: value }).then(response => {
        response.json().then(data => {
          setRows(rows.map(row => row._id === conditionId ? data : row));
        });
      });
    }
  }

  const handleAdd = () => {
    navigate(`/monitors/${monitorId}/conditions/add`);
  };


  const handleRowClick = (conditionId: string) => {
    navigate(`/monitors/${monitorId}/conditions/${conditionId}`);
  };

  const handleSave = () => {
    if (monitorId) {
      const updatedMonitor: any = {
        ...(monitor?.name !== name && { name }),
        ...(monitor?.description !== description && { description }),
        ...(monitor?.muted !== muted && { muted }),
        ...(monitor?.disabled !== disabled && { disabled }),
        fleets: selectedOptions.map(option => option.value),
      }

      MonitorService.updateMonitor(monitorId, updatedMonitor).then(response => {
        response.json().then(data => {
          setMonitor(data);
          toastSuccess(t('monitors.monitorUpdated'));
        });
      });
    } else {
      const newMonitor = {
        name,
        description,
        muted,
        disabled,
      }

      MonitorService.addMonitor(newMonitor).then(response =>
        response.json().then(data => {
          setMonitor(data);
          toastSuccess(t('monitors.monitorAdded'));
          navigate(`/monitors/${data._id}`);
        })
      );
    }
  };

  const fetchData = async () => {
    if (monitorId) {
      const response = await MonitorService.getMonitor(monitorId);
      const data = await response.json();
      setRows(data.conditions);
      setMonitor(data);

      setName(data.name);
      setDescription(data.description);
      setMuted(data.muted);
      setDisabled(data.disabled);

      setSelectedOptions(data.fleets.map((fleet: any) => ({
        value: fleet._id,
        label: fleet.name,
      })));
    }
  };

  const fetchFleets = async () => {
    const response = await FleetsService.getFleets();
    const data = await response.json();

    const fleets = data.map((fleet: any) => ({
      value: fleet._id,
      label: fleet.name,
    }));

    setFleets(fleets);
  };

  useEffect(() => {
    fetchData();
    fetchFleets();
  }, []);

  return (
    <>
      <div className={`pt-3 mx-auto max-w-6xl px-4 sm:px-6 lg:px-8`}>
        <Breadcrumb pages={breadcrumbPages}></Breadcrumb>
        <div className="flex flex-row items-center justify-between pt-3">
          <div>
            <h2 className="text-lg font-bold leading-6 text-gray-900">
              {t('monitors.monitor')}
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              {t('monitors.monitorDesc')}
            </p>
          </div>

          <button
            type="button"
            className={`rounded-md border border-transparent ${canSave ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-200'} py-2 px-3 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:col-start-2 sm:text-sm`}
            onClick={handleSave}
            disabled={!canSave}
          >
            {t('save')}
          </button>
        </div>

        <div className="mt-6 grid grid-cols-6 gap-6">
          <div className="col-span-4 sm:col-span-2">
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700"
            >
              {t('monitors.name')}
            </label>

            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
            />
          </div>

          <div className="col-span-8 sm:col-span-4">
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700"
            >
              {t('monitors.description')}
            </label>

            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-gray-900 focus:outline-none focus:ring-gray-900 sm:text-sm"
            />
          </div>

          <div className="flex flex-row col-span-12 sm:col-span-6 mb-6 grid grid-cols-6">
            <div className="col-span-4">
              <MultiSelect
                title={t('fleets.title')}
                options={fleets}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
              />
            </div>

            <div className="ml-auto col-span-1">
              <label
                htmlFor="muted" className="block text-sm font-medium text-gray-700">
                {t('monitors.muted')}
              </label>
              <input
                type="checkbox"
                checked={muted}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 w-5 h-5 rounded-md border border-gray-300"
                onChange={() => setMuted(!muted)}
              />
            </div>

            <div className="ml-auto col-span-1">
              <label
                htmlFor="disabled"
                className="block text-sm font-medium text-gray-700"
              >
                {t('monitors.disabled')}
              </label>

              <input
                type="checkbox"
                checked={disabled}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 w-5 h-5 rounded-md border border-gray-300"
                onChange={() => setDisabled(!disabled)}
              />
            </div>
          </div>
        </div>
      </div>

      {monitorId && (
        <Table
          columns={columns}
          rows={rows}
          setRows={setRows}
          title={t('monitors.monitorConditions')}
          showAddButton={true}
          onAdd={handleAdd}
          currentPage={1}
          pageSize={1}
          totalRows={1}
          showPagination={false}
          onRowClick={(row) => handleRowClick(row._id)}
        ></Table>
      )}
    </>
  );
}
